// src/context/SelectedOptionsContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  useCallback,
  useRef,
} from "react";
import { createUrlConfigString } from "../helpers/config-utilities";

import { translations } from "../helpers/translations";

const STORAGE_KEY = "selectedOptions_v1";
const STORAGE_KEY_LANG = "selectedLanguage_v1";

const SelectedOptionsContext = createContext(null);

function stableStringify(obj) {
  if (!obj || typeof obj !== "object") return String(obj);

  return JSON.stringify(
    Object.keys(obj)
      .sort()
      .reduce((acc, key) => {
        acc[key] = obj[key];
        return acc;
      }, {})
  );
}

function normalizeResponseString(str) {
  return str?.replace(/\s+/g, "");
}

function getGroupKeyFromDescriptor(descriptor) {
  if (!descriptor || typeof descriptor !== "object") return null;
  const keys = Object.keys(descriptor);
  return keys.length ? keys[0] : null; // e.g. "ExPt", "WhMo", ...
}

function reducer(state, action) {
  switch (action.type) {
    case "INIT":
      return action.payload || {};
    case "SELECT_OPTION": {
      const opt = action.payload; // { groupKey, name, price, image, descriptor, path? }
      return { ...state, [opt.groupKey]: opt };
    }
    case "REMOVE_OPTION": {
      const { [action.payload]: _, ...rest } = state;
      return rest;
    }
    case "CLEAR_ALL":
      return {};
    default:
      return state;
  }
}

const getInitialState = () => {
  try {
    const persistedState = localStorage.getItem(STORAGE_KEY);
    // If state exists in local storage, parse it.
    if (persistedState) {
      return JSON.parse(persistedState);
    }
  } catch (e) {
    console.error("Could not parse state from localStorage", e);
  }
  // Otherwise, return the default empty state.
  return {};
};

const detectCountryLanguage = () => {
  if (typeof navigator === "undefined") return "de";

  // prefer navigator.languages (more accurate)
  const locale =
    (navigator.languages && navigator.languages[0]) ||
    navigator.language ||
    "en";

  // locale examples:
  // "de-DE", "en-CH", "fr-AT", "en-US"
  const parts = locale.split("-");

  // const lang = parts[0].toLowerCase(); // "en", "de", "fr"
  const country = parts[1]?.toUpperCase(); // "DE", "CH", etc.

  // Countries that should default to German
  const germanCountries = ["DE", "AT", "CH"];

  if (germanCountries.includes(country)) return "de";

  // otherwise default to english
  return "en";
};

export function SelectedOptionsProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, getInitialState());
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [optionLoading, setOptionLoading] = useState(false);
  const pendingActionRef = useRef(null);
  const [language, setLanguage] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_LANG);
      if (stored) return stored;
    } catch {}

    return detectCountryLanguage();
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) dispatch({ type: "INIT", payload: JSON.parse(raw) });
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error("Could not save state to localStorage", e);
    }
  }, [state]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_LANG, language);
    } catch {}
  }, [language]);

  const items = useMemo(() => Object.values(state), [state]);
  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.price || 0), 0),
    [items]
  );

  const selectOption = useCallback(
    (raw) => {
      const groupKey =
        getGroupKeyFromDescriptor(raw?.descriptor) || raw.groupKey || "misc";
      const price = Number(raw?.price);
      const payload = { ...raw, price, groupKey };
      dispatch({ type: "SELECT_OPTION", payload });
    },
    [dispatch] // dispatch from useReducer is a stable reference
  );

  const removeOption = useCallback(
    (groupKey) => dispatch({ type: "REMOVE_OPTION", payload: groupKey }),
    [dispatch]
  );

  const clearAll = useCallback(
    () => dispatch({ type: "CLEAR_ALL" }),
    [dispatch]
  );

  useEffect(() => {
    const configString = createUrlConfigString(state);

    // --- Determine current URL state ---
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const currentConfigCode =
      pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : "";

    // Prevent update if the new string matches the current path parameter
    if (configString === currentConfigCode) return;

    // --- Construct New Path ---
    let newPathSegments = [...pathSegments];

    // If the last segment is the configCode, remove it before adding the new one
    if (currentConfigCode === currentConfigCode) {
      newPathSegments.pop();
    }

    // Add the new configuration string if it's not empty
    if (configString) {
      newPathSegments.push(configString);
    }

    // Construct the final URL (e.g., /config/ExPt-prm_FrLi-wcf)
    const newPath = "/" + newPathSegments.join("/") || "/";

    // Preserve any query parameters (like ?ar=16:9)
    const newUrl = newPath + window.location.search;

    // Use replaceState to change the URL without adding a new entry to browser history
    window.history.replaceState({ path: newUrl }, "", newUrl);
  }, [state]); // Run whenever the selected options map changes

  const startOptionAction = useCallback((descriptorValue) => {
    const targetString = normalizeResponseString(
      stableStringify(descriptorValue)
    );

    pendingActionRef.current = targetString;
    if (targetString.includes("Mod") || targetString.includes("Anim")) {
      setOptionLoading(true);
    } else {
      setOptionLoading(false);
    }
  }, []);

  // Called every time Unreal sends a response
  const completeOptionActionIfMatch = useCallback((responseValue) => {
    const responseString = normalizeResponseString(
      typeof responseValue === "string"
        ? responseValue
        : JSON.stringify(responseValue)
    );
    if (!pendingActionRef.current) return;
    if (responseString === undefined) setOptionLoading(false);

    if (pendingActionRef.current === responseString) {
      pendingActionRef.current = null;
      setOptionLoading(false);
    }
  }, []);

  // ---- summary popup controls (available app-wide)
  const openSummary = () => setSummaryOpen(true);
  const closeSummary = () => setSummaryOpen(false);
  const toggleSummary = () => setSummaryOpen((v) => !v);

  const toggleLanguage = (v) => setLanguage(v);

  const currentTranslations = translations[language];

  return (
    <SelectedOptionsContext.Provider
      value={{
        items,
        map: state,
        subtotal,
        selectOption,
        removeOption,
        clearAll,
        summaryOpen,
        openSummary,
        closeSummary,
        toggleSummary,
        language,
        toggleLanguage,
        currentTranslations,
        optionLoading,
        startOptionAction,
        completeOptionActionIfMatch,
      }}
    >
      {children}
    </SelectedOptionsContext.Provider>
  );
}

export function useSelectedOptions() {
  const ctx = useContext(SelectedOptionsContext);
  if (!ctx)
    throw new Error(
      "useSelectedOptions must be used within SelectedOptionsProvider"
    );
  return ctx;
}
